import { useNavigate, useParams } from 'react-router-dom'
import { NavBar } from 'components/NavBar/NavBar'
import { NoData } from 'components/NoData'
import { Container, ContainerContent, ContainerHeader } from 'components/Containers/Containers'
import { HonestTable, IHonestTableColumn } from 'components/HonestTable/HonestTable'
import { StatusPill } from 'components/ui/StatusPill/StatusPill'
import { Button } from 'components/ui/Button/Button'
import { MetricCard } from 'components/ui/MetricCard/MetricCard'
import { AlertListRow } from 'components/ui/AlertListRow/AlertListRow'
import { AlertList } from 'components/ui/AlertListRow/AlertListRow.styled'
import { DemoBadge } from 'pages/Operations/Shared/OpsSharedComponents'
import { BannerText, BannerWrap } from 'pages/Operations/Shared/opsShared.styled'
import { FuelKpiStrip } from 'pages/FuelManagement/FuelManagement.styled'
import { formatGhs, formatGhsCompact } from 'pages/FuelManagement/fuelOpsDemoData'
import { Flex, H1WithBack, PageWrapper } from 'styling/GlobalStyles'
import { BodyText, OtherText, TitleText } from 'styling/Typography'
import { tokens } from 'styling/tokens'
import { formatNumbersForDisplay } from 'utils/helpers'
import {
  IFuelConsumptionWindow,
  IFuelMachineDispense,
  IFuelMachineOperatorStat,
  IFuelMachineRuntimeDay,
  IFuelMachineTaskStat,
  IFuelMachineUnexplainedEvent,
} from 'types/fuelConsumption.interface'
import {
  bandDeltaPct,
  bandLabelOf,
  bandStateOf,
  buildConsumptionSeries,
  FUEL_CONSUMPTION_MACHINES,
  FUEL_MACHINE_DIESEL_GHS,
  FUEL_OPERATOR_FAIRNESS_CAPTION,
  FUEL_TASK_FOOTNOTE,
  isTelemetryDegraded,
  litresCostGhs,
  machineClassText,
  machineFuelCost30dGhs,
  operatorFairnessGuard,
  signedPctLabel,
} from './fuelConsumptionDemoData'
import { ModePill, ProvenanceBadge } from './ProvenanceBadge'
import {
  DaysToEmptyRow,
  DaysToEmptyValue,
  HeaderMetaRow,
  LegendRow,
  PanelStack,
  SectionLabel,
} from './FuelMachineDetail.styled'

const BAND_COLOR = {
  over: tokens.status.danger.fg,
  in: tokens.status.success.fg,
  under: tokens.text.muted,
}

const BAND_PILL = {
  over: 'danger',
  in: 'success',
  under: 'neutral',
} as const

const SEVERITY_WORD = {
  danger: 'High',
  warning: 'Medium',
  info: 'Low',
}

const fmt = (value: number): string => formatNumbersForDisplay(value)

const rateOf = (litres: number, hours: number): number => Math.round((litres / hours) * 10) / 10

export const FuelMachineDetail = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const machine = FUEL_CONSUMPTION_MACHINES.find((m) => m.id === Number(id))

  if (!machine) {
    return (
      <>
        <NavBar />
        <PageWrapper noFooter>
          <H1WithBack title='Machine fuel' arrow onClick={() => navigate('/fuel/machines')} />
          <NoData text='Machine not found — it may exist only on the connected backend.' />
        </PageWrapper>
      </>
    )
  }

  const degraded = isTelemetryDegraded(machine)
  const series = buildConsumptionSeries(machine)
  const isStatic = machine.machineClass === 'STATIC_PLANT'
  const avgState = bandStateOf(machine.avgRateLph, machine.nominalBurnRateLph)
  const avgDelta = bandDeltaPct(machine.avgRateLph, machine.nominalBurnRateLph)

  const handleExportCsv = () => {
    const quote = (value: string | number): string => `"${String(value).replace(/"/g, '""')}"`
    const header = ['Date', 'Time', 'Litres', 'SMU', 'Operator', 'Station', 'GHS'].map(quote).join(',')
    const lines = machine.dispenses.map((d) =>
      [d.dateLabel, d.time, d.litres, d.smuHours ?? '', d.operator, d.station, litresCostGhs(d.litres)]
        .map(quote)
        .join(','),
    )
    const blob = new Blob([[header, ...lines].join('\n')], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = `${machine.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')}-dispenses.csv`
    document.body.appendChild(anchor)
    anchor.click()
    document.body.removeChild(anchor)
    URL.revokeObjectURL(url)
  }

  const vsBandCell = (rate: number | null) => {
    if (rate == null) return <OtherText grey>—</OtherText>
    const state = bandStateOf(rate, machine.nominalBurnRateLph)
    return (
      <BodyText weight='bold' color={BAND_COLOR[state]}>
        {signedPctLabel(bandDeltaPct(rate, machine.nominalBurnRateLph))}
      </BodyText>
    )
  }

  const windowColumns: IHonestTableColumn<IFuelConsumptionWindow>[] = [
    {
      id: 'period',
      header: 'PERIOD',
      sticky: true,
      minWidth: '12rem',
      cell: (item) => <BodyText weight='600'>{item.periodLabel}</BodyText>,
    },
    {
      id: 'hours',
      header: 'HOURS',
      align: 'right',
      cell: (item) => (item.hours != null ? <BodyText>{`${item.hours} h`}</BodyText> : <OtherText grey>—</OtherText>),
    },
    {
      id: 'litres',
      header: 'LITRES',
      align: 'right',
      cell: (item) => <BodyText weight='bold'>{`${fmt(item.litres)} L`}</BodyText>,
    },
    {
      id: 'rate',
      header: 'L/H',
      minWidth: '11rem',
      cell: (item) =>
        item.unusable ? (
          <StatusPill status='warning'>SMU MISSING</StatusPill>
        ) : item.ratePerHour == null ? (
          <OtherText grey>—</OtherText>
        ) : (
          <Flex center gap='0.5rem'>
            <BodyText weight='bold'>{item.ratePerHour}</BodyText>
            <ProvenanceBadge provenance={item.provenance} />
          </Flex>
        ),
    },
    {
      id: 'vsNominal',
      header: 'VS NOMINAL',
      align: 'right',
      cell: (item) => vsBandCell(item.ratePerHour),
    },
    {
      id: 'context',
      header: 'CONTEXT',
      minWidth: '18rem',
      cell: (item) => <OtherText grey>{item.context}</OtherText>,
    },
  ]

  const dispenseColumns: IHonestTableColumn<IFuelMachineDispense>[] = [
    {
      id: 'when',
      header: 'WHEN',
      minWidth: '9rem',
      cell: (item) => <BodyText weight='600'>{`${item.dateLabel} · ${item.time}`}</BodyText>,
    },
    {
      id: 'litres',
      header: 'LITRES',
      align: 'right',
      cell: (item) => <BodyText weight='bold'>{`${fmt(item.litres)} L`}</BodyText>,
    },
    {
      id: 'smu',
      header: 'SMU READING',
      align: 'right',
      minWidth: '9rem',
      cell: (item) =>
        item.smuHours != null ? (
          <BodyText>{`${fmt(item.smuHours)} h`}</BodyText>
        ) : (
          <StatusPill status='warning'>— missing</StatusPill>
        ),
    },
    { id: 'operator', header: 'OPERATOR', cell: (item) => item.operator },
    { id: 'station', header: 'STATION', cell: (item) => item.station },
    {
      id: 'cost',
      header: 'GH₵',
      align: 'right',
      cell: (item) => <BodyText>{formatGhs(litresCostGhs(item.litres))}</BodyText>,
    },
  ]

  const operatorColumns: IHonestTableColumn<IFuelMachineOperatorStat>[] = [
    {
      id: 'operator',
      header: 'OPERATOR',
      sticky: true,
      minWidth: '9rem',
      cell: (item) => <BodyText weight='600'>{item.name}</BodyText>,
    },
    { id: 'windows', header: 'WINDOWS', align: 'right', cell: (item) => `${item.windows}` },
    { id: 'hours', header: 'HOURS', align: 'right', cell: (item) => `${item.hours} h` },
    {
      id: 'litres',
      header: 'LITRES',
      align: 'right',
      cell: (item) => <BodyText>{`${fmt(item.litres)} L`}</BodyText>,
    },
    {
      id: 'rate',
      header: 'L/H',
      minWidth: '14rem',
      cell: (item) =>
        operatorFairnessGuard(item) ? (
          <OtherText grey>{`Not enough data yet (${item.windows} windows · ${item.hours} h)`}</OtherText>
        ) : (
          <Flex center gap='0.5rem'>
            <BodyText weight='bold'>{item.ratePerHour}</BodyText>
            <ProvenanceBadge provenance='ATTRIBUTED' />
          </Flex>
        ),
    },
    {
      id: 'vsAvg',
      header: 'VS MACHINE AVG',
      align: 'right',
      cell: (item) => {
        if (operatorFairnessGuard(item)) return <OtherText grey>—</OtherText>
        const delta = Math.round(((item.ratePerHour - machine.avgRateLph) / machine.avgRateLph) * 1000) / 10
        const color = delta > 5 ? tokens.status.warning.fg : delta < -5 ? tokens.status.success.fg : tokens.text.muted
        return (
          <BodyText weight='bold' color={color}>
            {signedPctLabel(delta)}
          </BodyText>
        )
      },
    },
  ]

  const taskColumns: IHonestTableColumn<IFuelMachineTaskStat>[] = [
    {
      id: 'context',
      header: 'PLAN / TASK CONTEXT',
      sticky: true,
      minWidth: '18rem',
      cell: (item) => <BodyText weight='600'>{item.context}</BodyText>,
    },
    { id: 'hours', header: 'HOURS', align: 'right', cell: (item) => `${item.hours} h` },
    {
      id: 'litres',
      header: 'ATTRIBUTED LITRES',
      align: 'right',
      minWidth: '10rem',
      cell: (item) => (
        <Flex center justend gap='0.5rem'>
          <BodyText weight='bold'>{`${fmt(item.litres)} L`}</BodyText>
          <ProvenanceBadge provenance='ATTRIBUTED' />
        </Flex>
      ),
    },
    {
      id: 'cost',
      header: 'GH₵',
      align: 'right',
      cell: (item) => <BodyText>{formatGhs(litresCostGhs(item.litres))}</BodyText>,
    },
    { id: 'share', header: 'SHARE', align: 'right', cell: (item) => `${item.sharePct}%` },
  ]

  const runtimeColumns: IHonestTableColumn<IFuelMachineRuntimeDay>[] = [
    {
      id: 'date',
      header: 'DAY',
      sticky: true,
      minWidth: '8rem',
      cell: (item) => <BodyText weight='600'>{item.dateLabel}</BodyText>,
    },
    { id: 'runtime', header: 'RUNTIME', align: 'right', cell: (item) => `${item.runtimeHours} h` },
    {
      id: 'litres',
      header: 'LITRES',
      align: 'right',
      cell: (item) => <BodyText weight='bold'>{`${fmt(item.litres)} L`}</BodyText>,
    },
    {
      id: 'rate',
      header: 'L/H VS NOMINAL',
      align: 'right',
      minWidth: '10rem',
      cell: (item) => {
        const rate = rateOf(item.litres, item.runtimeHours)
        const state = bandStateOf(rate, machine.nominalBurnRateLph)
        return (
          <BodyText weight='bold' color={BAND_COLOR[state]}>
            {`${rate} L/h · ${signedPctLabel(bandDeltaPct(rate, machine.nominalBurnRateLph))}`}
          </BodyText>
        )
      },
    },
  ]

  const unexplainedColumns: IHonestTableColumn<IFuelMachineUnexplainedEvent>[] = [
    {
      id: 'when',
      header: 'WHEN',
      minWidth: '8rem',
      cell: (item) => <BodyText weight='600'>{item.dateLabel}</BodyText>,
    },
    { id: 'window', header: 'WINDOW', cell: (item) => item.windowLabel },
    {
      id: 'litres',
      header: 'LITRES',
      align: 'right',
      cell: (item) => (
        <BodyText weight='bold' color={tokens.status.danger.fg}>{`${fmt(item.litres)} L`}</BodyText>
      ),
    },
    {
      id: 'flag',
      header: 'FLAG',
      cell: () => (
        <StatusPill status='danger' dot>
          UNEXPLAINED
        </StatusPill>
      ),
    },
    {
      id: 'note',
      header: 'NOTE',
      minWidth: '16rem',
      cell: (item) => <OtherText grey>{item.note}</OtherText>,
    },
  ]

  return (
    <>
      <NavBar />
      <PageWrapper noFooter>
        <H1WithBack title={machine.name} arrow onClick={() => navigate('/fuel/machines')} />
        <HeaderMetaRow>
          <OtherText grey>
            {`${machineClassText(machine)} · ${machine.model} · nominal ${machine.nominalBurnRateLph} L/h · healthy band ±15%`}
          </OtherText>
          <ModePill mode={machine.fuelDataMode} />
          <DemoBadge />
        </HeaderMetaRow>
        <LegendRow>
          <OtherText grey>Every rate carries its source:</OtherText>
          <ProvenanceBadge provenance='MEASURED' />
          <ProvenanceBadge provenance='BETWEEN_FILLS' />
          <ProvenanceBadge provenance='ATTRIBUTED' />
        </LegendRow>
        {degraded && (
          <BannerWrap $tone='warning'>
            <BannerText $tone='warning'>
              {`Sensor offline since ${machine.telemetrySilentSince} — showing between-fills math`}
            </BannerText>
          </BannerWrap>
        )}
        {machine.note && <OtherText color={tokens.text.muted}>{machine.note}</OtherText>}
        <PanelStack>
          <Container>
            <ContainerHeader>
              <TitleText weight='bold'>Consumption</TitleText>
            </ContainerHeader>
            <ContainerContent gap='1rem'>
              <FuelKpiStrip>
                <MetricCard
                  value={`${fmt(machine.litres30d)} L`}
                  label='30D LITRES'
                  sublabel={`≈ ${fmt(Math.round(machine.litres30d / 30))} L/day`}
                  accentColor={tokens.accent.base}
                />
                <MetricCard
                  value={`${machine.avgRateLph} L/h`}
                  label='AVG BURN'
                  sublabel={`nominal ${machine.nominalBurnRateLph} ±15%`}
                  accentColor={BAND_COLOR[avgState]}
                  trailing={
                    <StatusPill status={BAND_PILL[avgState]}>
                      {`${signedPctLabel(avgDelta)} ${bandLabelOf(machine.avgRateLph, machine.nominalBurnRateLph)}`}
                    </StatusPill>
                  }
                />
                <MetricCard
                  value={formatGhsCompact(machineFuelCost30dGhs(machine))}
                  label='30D FUEL COST'
                  sublabel={`diesel GH₵ ${FUEL_MACHINE_DIESEL_GHS.toFixed(2)}/L`}
                  accentColor={tokens.accent.base}
                />
                <MetricCard
                  value={machine.lastFillLabel}
                  label='LAST FILL'
                  sublabel={machine.dispenses[0] ? `${machine.dispenses[0].station} · ${machine.dispenses[0].operator}` : '—'}
                  accentColor={tokens.status.success.fg}
                />
              </FuelKpiStrip>
              <SectionLabel>
                {machine.fuelDataMode === 'TELEMETRY' && !degraded ? 'Measured segments' : 'Between-fills windows'}
              </SectionLabel>
              <HonestTable
                columns={windowColumns}
                data={series}
                keyField='id'
                emptyMessage='No burn windows yet — they appear after the second fill.'
              />
              <Flex between center width='100%'>
                <SectionLabel>Dispense log — this machine</SectionLabel>
                <Button variant='secondary' size='sm' onClick={handleExportCsv}>Export CSV</Button>
              </Flex>
              <HonestTable
                columns={dispenseColumns}
                data={machine.dispenses}
                keyField='id'
                emptyMessage='No dispenses recorded yet.'
              />
            </ContainerContent>
          </Container>

          {!isStatic && (
            <Container>
              <ContainerHeader>
                <TitleText weight='bold'>By operator</TitleText>
              </ContainerHeader>
              <ContainerContent gap='0.75rem'>
                <HonestTable
                  columns={operatorColumns}
                  data={machine.operators}
                  keyField='id'
                  emptyMessage='No operator windows yet.'
                />
                <OtherText grey>{FUEL_OPERATOR_FAIRNESS_CAPTION}</OtherText>
              </ContainerContent>
            </Container>
          )}

          {!isStatic && (
            <Container>
              <ContainerHeader>
                <TitleText weight='bold'>By task / plan</TitleText>
              </ContainerHeader>
              <ContainerContent gap='0.75rem'>
                <HonestTable
                  columns={taskColumns}
                  data={machine.tasks}
                  keyField='id'
                  emptyMessage='No task attribution yet.'
                />
                <OtherText color={tokens.text.muted}>{FUEL_TASK_FOOTNOTE}</OtherText>
              </ContainerContent>
            </Container>
          )}

          {isStatic && (
            <Container>
              <ContainerHeader>
                <TitleText weight='bold'>Runtime log</TitleText>
              </ContainerHeader>
              <ContainerContent gap='1rem'>
                <HonestTable
                  columns={runtimeColumns}
                  data={machine.runtimeDays}
                  keyField='id'
                  emptyMessage='No runtime entries yet.'
                />
                <SectionLabel>Unexplained events</SectionLabel>
                {machine.unexplainedEvents.length ? (
                  <HonestTable columns={unexplainedColumns} data={machine.unexplainedEvents} keyField='id' />
                ) : (
                  <OtherText grey>No unexplained level drops — every litre this week is accounted for.</OtherText>
                )}
                <DaysToEmptyRow>
                  <DaysToEmptyValue>
                    {machine.daysToEmpty != null ? `~${machine.daysToEmpty} days to empty` : 'Days to empty —'}
                  </DaysToEmptyValue>
                  {machine.nextRefillNote && <OtherText grey>{machine.nextRefillNote}</OtherText>}
                </DaysToEmptyRow>
              </ContainerContent>
            </Container>
          )}

          <Container>
            <ContainerHeader>
              <TitleText weight='bold'>Alerts</TitleText>
            </ContainerHeader>
            <ContainerContent gap='0.75rem'>
              {machine.alerts.length ? (
                <AlertList>
                  {machine.alerts.map((a) => (
                    <AlertListRow
                      key={a.id}
                      title={a.text}
                      meta={a.latency}
                      pillLabel={a.maintenanceAction ? 'Raise request' : SEVERITY_WORD[a.severity]}
                      pillTone={a.severity}
                      onPillClick={a.maintenanceAction ? () => navigate('/operations/today') : undefined}
                    />
                  ))}
                </AlertList>
              ) : (
                <OtherText grey>No machine alerts — telemetry alerts land live, manual-math alerts land at the next fill.</OtherText>
              )}
            </ContainerContent>
          </Container>
        </PanelStack>
      </PageWrapper>
    </>
  )
}
